import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Icon from '../Icon/Icon';
import Checkbox from '../Checkbox/Checkbox';
import * as S from './DynamicList.styles';
import { useCallback } from 'react';
import useDynamicListEvent from './Hook/useDynamicListEvent.inner';

const DynamicList = ({
  onChange,
  defaultValue,
  disabled,
  label,
  data,
  draggable,
}) => {
  const [items, setItems] = useState();
  const refDynamicList = useRef(null);
  const { setRefElement } = useDynamicListEvent();

  useEffect(() => {
    setItems(data);
  }, [data]);

  useEffect(() => {
    onChange && onChange(items);
  }, [items]);

  useEffect(() => {
    refDynamicList.current && setRefElement(refDynamicList);
  }, [refDynamicList.current]);

  const cleanItems = (item) => ({
    ...item,
    active: false,
    draggable: null,
    draggableOver: null,
    isDraggedBottom: null,
    isDraggedTop: null,
  });

  const getActiveItems = useCallback((items) => {
    const currentItems = items;
    const active = currentItems.find((item) => !!item.active);
    currentItems.splice(
      active.draggableOver,
      0,
      currentItems.splice(active.draggable, 1)[0]
    );
    return currentItems.map(cleanItems);
  });

  const handleOnDragStart = (event, index) => {
    setItems((items) =>
      items.map((item, indexItem) => ({
        ...item,
        active: indexItem === index,
        draggable: index,
      }))
    );
  };

  const handleOnDragEnter = (event, index) => {
    setItems(
      items.map((item, indexItem) => ({
        ...item,
        draggableOver: index,
        isDraggedBottom: indexItem === index && index > item.draggable,
        isDraggedTop: indexItem === index && index < item.draggable,
      }))
    );
  };

  const handleOnDragEnd = (event) => {
    setItems((items) => getActiveItems(items));
  };

  const getRenderCard = ({
    isDraggedTop,
    value,
    disabled,
    checked,
    label,
    active,
    isDraggedBottom,
  }) => (
    <>
      {isDraggedTop && <S.DynamicListItemPlaceHolder />}
      <S.DynamicListItem isDraggable={draggable}>
        <S.DynamicListItemValue>
          <Checkbox id={value} disabled={disabled} checked={checked}>
            {label}
          </Checkbox>
        </S.DynamicListItemValue>
        {draggable && (
          <S.DynamicListItemDraggable>
            <Icon
              name='draggable_dots'
              variant={active ? 'primary' : 'neutral'}
            />
          </S.DynamicListItemDraggable>
        )}
      </S.DynamicListItem>
      {isDraggedBottom && <S.DynamicListItemPlaceHolder />}
    </>
  );

  return (
    <S.DynamicListWrapper ref={refDynamicList}>
      {!!items &&
        items.map((item, index) => (
          <S.DynamicItemWrapper
            key={index}
            draggable={draggable}
            onDragStart={(event) => handleOnDragStart(event, index)}
            onDragEnter={(event) => handleOnDragEnter(event, index)}
            onDragEnd={(event) => handleOnDragEnd(event)}
            onMouseDown={(event) => handleOnDragStart(event, index)}
            data-testid={'dynamic-item-container'}
          >
            {getRenderCard(item)}
          </S.DynamicItemWrapper>
        ))}
    </S.DynamicListWrapper>
  );
};

DynamicList.defaultProps = {
  draggable: true,
};

DynamicList.propTypes = {
  defaultValue: PropTypes.array,
  draggable: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
};

export default DynamicList;
